import { alpha } from '@material-ui/core';

export const isGradient = (value) => {
	return (
		value?.startsWith('linear-gradient') || value?.startsWith('radial-gradient')
	);
};

export const alphaExtended = (value, alphaValue) => {
	if (value.startsWith('linear-gradient')) {
		const gradientColors = value.match(/rgba?\([^()]+\)/g); // extracts rgba or rgb values
		if (gradientColors && gradientColors.length === 2) {
			const [start, end] = gradientColors;
			const startWithAlpha = alpha(start, alphaValue);
			const endWithAlpha = alpha(end, alphaValue);
			return value.replace(start, startWithAlpha).replace(end, endWithAlpha);
		} else {
			console.error('Gradient format not recognized.');
			return value;
		}
	} else {
		return alpha(value, alphaValue);
	}
};

export const placeBackgroundColor = (color) => {
	return {
		backgroundColor: `${isGradient(color) ? 'transparent' : color} !important`,
		background: `${isGradient(color) ? color : undefined} !important`
	};
};

export function getHostname() {
	return localStorage.getItem('host') || window.location.hostname;
}

export const formatToOneDecimal = (value) => {
  return typeof value === 'number' ? parseFloat(value.toFixed(1)) : value;
};

/**
 * Downloads a JSON file with the specified data and filename.
 *
 * @param {Object} data - The data to be converted to JSON and downloaded.
 * @param {string} [filename='data.json'] - The name of the file to be downloaded. Defaults to 'data.json'.
 *
 * @returns {void} This function does not return a value.
 *
 * @example
 * const myData = { name: "John", age: 30 };
 * downloadJson(myData, 'myData.json');
 */
export const downloadJson = (data, filename = 'data.json') => {
  // 1. Convert the data to a JSON string
  // The null, 2 arguments make it readable (pretty-print)
  const jsonString = JSON.stringify(data, null, 2);

  // 2. Create a Blob with the JSON content
  const blob = new Blob([jsonString], { type: 'application/json' });

  // 3. Create a temporary URL for the Blob
  const url = URL.createObjectURL(blob);

  // 4. Create a hidden link element
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  // 5. Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // 6. Clean up the URL object to free memory
  URL.revokeObjectURL(url);
};


/**
 * Reads and parses a JSON file using FileReader API
 * @param {File} file - The File object to be read and parsed
 * @returns {Promise<Object>} A promise that resolves with the parsed JSON data or rejects with an error
 * @throws {Error} Throws an error if the file cannot be read or if JSON parsing fails
 */
export const readJsonFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const jsonData = JSON.parse(event.target.result);
        resolve(jsonData);
      } catch (error) {
        reject(new Error('Error parsing JSON file: ' + error.message));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file: ' + reader.error));
    };

    reader.readAsText(file);
  });
};


/**
 * Recursively removes NaN values from objects and arrays.
 * 
 * This function traverses through nested objects and arrays, filtering out any NaN values
 * while preserving the structure of non-NaN data. Numbers, strings, booleans, and other
 * primitive types are retained as-is. For arrays, NaN items are completely removed. For
 * objects, properties with NaN values are omitted from the result.
 * 
 * @param {*} obj - The input value to clean. Can be any type: primitive, array, or object.
 * @returns {*} A new value with NaN values removed. Returns the same type as input
 *              (primitives returned as-is, arrays and objects are new instances).
 * 
 * @example
 * // Removing NaN from an object
 * const obj = { a: 1, b: NaN, c: 'hello' };
 * removeNaNValues(obj); // Returns: { a: 1, c: 'hello' }
 * 
 * @example
 * // Removing NaN from an array
 * const arr = [1, NaN, 3, NaN, 5];
 * removeNaNValues(arr); // Returns: [1, 3, 5]
 * 
 * @example
 * // Handling nested structures
 * const nested = {
 *   user: { name: 'John', age: NaN, score: 95 },
 *   values: [10, NaN, 20, 30]
 * };
 * removeNaNValues(nested);
 * // Returns:
 * // {
 * //   user: { name: 'John', score: 95 },
 * //   values: [10, 20, 30]
 * // }
 */
export const removeNaNValues = (obj) => {
  // Base case: if it's a primitive or null, return it as-is
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  // Handle Arrays: Filter out NaNs, then recursively clean remaining items
  if (Array.isArray(obj)) {
    return obj
      .filter(item => !isNaN(item))
      .map(item => removeNaNValues(item));
  }

  // Handle Objects: Build a new object without NaN values
  return Object.entries(obj).reduce((acc, [key, value]) => {
    // Only copy over the key/value if the value is NOT strictly NaN
    if (!isNaN(value)) {
      acc[key] = removeNaNValues(value);
    }
    return acc;
  }, {});
};
